import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSelector } from "react-redux";

import instance from "../../../api/instance";
import { RootState } from "../../../redux/store";
import { useSyncGlobalPageLoader } from "../../../contexts/global-page-loader-context";
import "react-datepicker/dist/react-datepicker.css";

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100, 125, 150];

type FilterType = "all" | "created_at" | "last_login";
type SortDirection = "asc" | "desc";
type SortKey = keyof UserRegisterRow;

interface UserRegisterRow {
  user_name: string;
  agent_name: string;
  created_date: string;
  last_login: string;
  first_deposit_date: string;
  last_deposit_date: string;
  deposit: string;
  sports_balance: number;
  casino_balance: number;
  third_party_credit_balance: number;
  sport_book_balance: number | "";
}

interface IClientOption {
  user_id: string;
  user_name: string;
  display_name: string;
}

const toNumber = (value: unknown) => Number(value ?? 0);

const getDefaultDateRange = (): [Date, Date] => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 10);
  return [start, end];
};

const formatDate = (value: unknown) => {
  if (!value) return "-";
  const str = String(value);
  if (/^\d+$/.test(str)) {
    const unix = Number(str);
    const date = new Date(unix * 1000);
    if (!Number.isNaN(date.getTime())) {
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();
      const hh = String(date.getHours()).padStart(2, "0");
      const mi = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");
      return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
    }
  }
  return str;
};

const getBalanceClass = (value: number | "") => {
  if (value === "") return "";
  if (value < 0) return "text-danger";
  return "text-success";
};

const DATE_SORT_KEYS: SortKey[] = [
  "created_date",
  "last_login",
  "first_deposit_date",
  "last_deposit_date",
];

const NUMERIC_SORT_KEYS: SortKey[] = [
  "sports_balance",
  "casino_balance",
  "third_party_credit_balance",
  "sport_book_balance",
];

const parseDisplayDateToTimestamp = (value: unknown) => {
  const str = String(value ?? "").trim();
  if (!str || str === "-") return null;
  const match = str.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}):(\d{2}))?$/
  );
  if (match) {
    const [, dd, mm, yyyy, hh = "00", mi = "00", ss = "00"] = match;
    const dt = new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(mi),
      Number(ss)
    );
    const time = dt.getTime();
    if (!Number.isNaN(time)) return time;
  }
  const fallback = new Date(str).getTime();
  return Number.isNaN(fallback) ? null : fallback;
};

const EXPORT_HEADERS = [
  "User Name",
  "Agent Name",
  "Created Date",
  "Last Login",
  "First Deposit Date",
  "Last Deposit Date",
  "Deposit",
  "Sports Balance",
  "Casino Balance",
  "Third Party Credit Balance",
  "Sport Book Balance",
];

const CLIENT_SEARCH_MIN_LENGTH = 2;

const escapeCsvField = (field: unknown) => {
  const str = String(field ?? "");
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const UserRegisterDetailPage = () => {
  const loggedInUserId = useSelector((state: RootState) => state.user.userData?.user_id ?? "");
  const [clientSearchText, setClientSearchText] = useState("");
  const [clientOptions, setClientOptions] = useState<IClientOption[]>([]);
  const [selectedClient, setSelectedClient] = useState<IClientOption | null>(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => getDefaultDateRange());
  const [rows, setRows] = useState<UserRegisterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(25);

  useSyncGlobalPageLoader(loading);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: SortDirection } | null>(null);
  const clientWrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clientSearchRequestIdRef = useRef(0);
  const hasInitialLoadRunRef = useRef(false);

  const [startDate, endDate] = dateRange;

  const fetchClients = useCallback(
    async (queryValue: string) => {
      if (!loggedInUserId) {
        return [] as IClientOption[];
      }
      const requestId = ++clientSearchRequestIdRef.current;
      try {
        const { data } = await instance.get("admin/downline-members", {
          params: {
            adminId: loggedInUserId,
            memberType: "admin",
            scope: "recursive",
            search: queryValue,
          },
        });

        const rawList: any[] = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.data?.rows)
            ? data.data.rows
            : Array.isArray(data?.data?.members)
              ? data.data.members
              : [];

        const list: IClientOption[] = rawList
          .map((item) => ({
            user_id: item?.user_id ?? item?.admin_id ?? item?.id ?? "",
            user_name: item?.user_name ?? item?.username ?? "",
            display_name:
              item?.display_name ?? item?.displayName ?? item?.user_name ?? item?.username ?? "",
          }))
          .filter((item) => item.user_id);

        if (requestId !== clientSearchRequestIdRef.current) return [] as IClientOption[];
        return list;
      } catch {
        if (requestId !== clientSearchRequestIdRef.current) return [] as IClientOption[];
        return [] as IClientOption[];
      }
    },
    [loggedInUserId]
  );

  const handleClientSearchChange = (value: string) => {
    setClientSearchText(value);
    if (selectedClient) {
      setSelectedClient(null);
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const q = value.toLowerCase().trim();
      if (q.length < CLIENT_SEARCH_MIN_LENGTH) {
        setClientOptions([]);
        setShowClientDropdown(true);
        return;
      }
      const source = await fetchClients(q);
      setClientOptions(source);
      setShowClientDropdown(true);
    }, 200);
  };

  const handleSelectClient = (client: IClientOption) => {
    setSelectedClient(client);
    setClientSearchText(client.display_name || client.user_name);
    setShowClientDropdown(false);
  };

  const fetchUserRegisterDetails = useCallback(async () => {
    const adminId = selectedClient?.user_id || loggedInUserId;
    if (!adminId) {
      setRows([]);
      return;
    }

    const params: Record<string, string | number> = { adminId };
    if (filterType !== "all") {
      params.type = filterType;
      if (startDate) {
        const from = new Date(startDate);
        from.setHours(0, 0, 0, 0);
        params.startDate = Math.floor(from.getTime() / 1000);
      }
      if (endDate) {
        const to = new Date(endDate);
        to.setHours(23, 59, 59, 999);
        params.endDate = Math.floor(to.getTime() / 1000);
      }
    }

    setLoading(true);
    try {
      const { data } = await instance.get("admin/downline-users-by-admin", {
        params,
      });

      const rawList: any[] = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.rows)
          ? data.data.rows
          : Array.isArray(data?.data?.list)
            ? data.data.list
            : Array.isArray(data?.data?.userData)
              ? data.data.userData
            : [];

      const mapped: UserRegisterRow[] = rawList.map((item) => ({
        user_name: item?.user_name ?? item?.username ?? "-",
        agent_name: item?.agent_name ?? item?.admin_name ?? item?.up_line_name ?? item?.upline_admin ?? "-",
        created_date: formatDate(item?.created_at ?? item?.created_date),
        last_login: formatDate(item?.last_login ?? item?.last_login_date),
        first_deposit_date: formatDate(item?.first_deposit_date ?? item?.first_deposit_at),
        last_deposit_date: formatDate(item?.last_deposit_date ?? item?.last_deposit_at),
        deposit: item?.deposit_label ?? item?.deposit ?? "No Deposit",
        sports_balance: toNumber(item?.sports_balance ?? item?.sport_balance),
        casino_balance: toNumber(item?.casino_balance),
        third_party_credit_balance: toNumber(item?.third_party_credit_balance ?? item?.third_party_balance),
        sport_book_balance: item?.sport_book_balance ?? "",
      }));

      setRows(mapped);
      setCurrentPage(1);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedClient, loggedInUserId, filterType, startDate, endDate]);

  const handleLoad = async (e: FormEvent) => {
    e.preventDefault();
    await fetchUserRegisterDetails();
  };

  const handleReset = () => {
    setClientSearchText("");
    setClientOptions([]);
    setSelectedClient(null);
    setShowClientDropdown(false);
    setFilterType("all");
    setDateRange(getDefaultDateRange());
    setRows([]);
    setSearchText("");
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!loggedInUserId || hasInitialLoadRunRef.current) return;
    hasInitialLoadRunRef.current = true;
    fetchUserRegisterDetails();
  }, [loggedInUserId, fetchUserRegisterDetails]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (clientWrapperRef.current && !clientWrapperRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredList = useMemo(() => {
    const q = searchText.toLowerCase().trim();
    const c = clientSearchText.toLowerCase().trim();

    return rows.filter((row) => {
      const searchMatch =
        !q ||
        [
          row.user_name,
          row.agent_name,
          row.created_date,
          row.last_login,
          row.deposit,
          String(row.sports_balance),
          String(row.casino_balance),
          String(row.third_party_credit_balance),
        ].some((value) => value.toLowerCase().includes(q));

      const clientMatch =
        !c ||
        row.user_name.toLowerCase().includes(c) ||
        row.agent_name.toLowerCase().includes(c);

      return searchMatch && clientMatch;
    });
  }, [rows, searchText, clientSearchText]);

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
    setCurrentPage(1);
  };

  const handleSortKeyDown = (e: React.KeyboardEvent<HTMLTableCellElement>, key: SortKey) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSort(key);
    }
  };

  const sortedFilteredList = useMemo(() => {
    if (!sortConfig) return filteredList;
    const { key, direction } = sortConfig;
    const directionMultiplier = direction === "asc" ? 1 : -1;
    const sorted = [...filteredList].sort((a, b) => {
      let aValue: string | number = String(a[key] ?? "").toLowerCase();
      let bValue: string | number = String(b[key] ?? "").toLowerCase();

      if (DATE_SORT_KEYS.includes(key)) {
        aValue = parseDisplayDateToTimestamp(a[key]) ?? Number.NEGATIVE_INFINITY;
        bValue = parseDisplayDateToTimestamp(b[key]) ?? Number.NEGATIVE_INFINITY;
      } else if (NUMERIC_SORT_KEYS.includes(key)) {
        const aNum = Number(a[key] ?? 0);
        const bNum = Number(b[key] ?? 0);
        aValue = Number.isNaN(aNum) ? Number.NEGATIVE_INFINITY : aNum;
        bValue = Number.isNaN(bNum) ? Number.NEGATIVE_INFINITY : bNum;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * directionMultiplier;
      }

      return String(aValue).localeCompare(String(bValue), undefined, {
        sensitivity: "base",
        numeric: true,
      }) * directionMultiplier;
    });
    return sorted;
  }, [filteredList, sortConfig]);

  const getHeaderAriaSort = (key: SortKey): "none" | "ascending" | "descending" => {
    if (!sortConfig || sortConfig.key !== key) return "none";
    return sortConfig.direction === "asc" ? "ascending" : "descending";
  };

  const getHeaderHint = (key: SortKey) => {
    if (!sortConfig || sortConfig.key !== key || sortConfig.direction === "desc") {
      return " (Click to sort ascending)";
    }
    return " (Click to sort descending)";
  };

  const totalPages = Math.max(1, Math.ceil(sortedFilteredList.length / pageSize));
  const pagedList = sortedFilteredList.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  const exportRows = sortedFilteredList.map((row) => [
    row.user_name,
    row.agent_name,
    row.created_date,
    row.last_login,
    row.first_deposit_date,
    row.last_deposit_date,
    row.deposit,
    row.sports_balance,
    row.casino_balance,
    row.third_party_credit_balance,
    row.sport_book_balance,
  ]);

  const handleExportExcel = () => {
    const header = EXPORT_HEADERS.map(escapeCsvField).join(",");
    const body = exportRows.map((row) => row.map(escapeCsvField).join(","));
    const csvContent = [header, ...body].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "user-register-detail.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = () => {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF({ orientation: "landscape", format: "a4" });
    autoTable(doc, {
      head: [EXPORT_HEADERS],
      body: exportRows,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [25, 103, 210] },
      margin: { top: 15 },
    });
    doc.save("user-register-detail.pdf");
  };

  return (
    <div>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-0 font-size-18">User Register Detail</h4>
            <div className="page-title-right" />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="report-form mb-3">
                <form method="post" className="ajaxFormSubmit" onSubmit={handleLoad}>
                  <div className="row row5">
                    <div className="col-2">
                      <div
                        className="form-group user-lock-search"
                        style={{ position: "relative" }}
                        ref={clientWrapperRef}
                      >
                        <label htmlFor="client-name-search">Search By Client Name</label>
                        <input
                          id="client-name-search"
                          type="text"
                          autoComplete="off"
                          spellCheck={false}
                          placeholder="Select option"
                          className="form-control"
                          value={clientSearchText}
                          onChange={(e) => handleClientSearchChange(e.target.value)}
                          onFocus={() => {
                            setClientOptions([]);
                            clientSearchRequestIdRef.current += 1;
                            setShowClientDropdown(true);
                          }}
                        />
                        {showClientDropdown && (
                          <ul
                            className="list-group"
                            style={{
                              position: "absolute",
                              zIndex: 1000,
                              width: "100%",
                              maxHeight: "300px",
                              overflowY: "auto",
                              boxShadow: "0 2px 8px rgba(0,0,0,.15)",
                            }}
                          >
                            {clientOptions.length === 0 ? (
                              <li className="list-group-item">
                                {clientSearchText.trim()
                                  ? "No elements found. Consider changing the search query."
                                  : "List is empty."}
                              </li>
                            ) : (
                              clientOptions.map((client) => (
                                <li key={client.user_id} className="list-group-item list-group-item-action">
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-left w-100"
                                    style={{ textDecoration: "none", color: "inherit" }}
                                    onClick={() => handleSelectClient(client)}
                                  >
                                    {client.display_name || client.user_name}
                                  </button>
                                </li>
                              ))
                            )}
                          </ul>
                        )}
                      </div>
                    </div>
                    <div className="col-2">
                      <div className="form-group">
                        <label htmlFor="user-register-type">Type</label>
                        <select
                          id="user-register-type"
                          className="form-control"
                          value={filterType}
                          onChange={(e) => setFilterType(e.target.value as FilterType)}
                        >
                          <option value="all">All</option>
                          <option value="created_at">Created Date</option>
                          <option value="last_login">Last Login Date</option>
                        </select>
                      </div>
                    </div>
                    {filterType !== "all" && (
                      <div className="col-3">
                        <label htmlFor="user-register-date-range">Select Date Range</label>
                        <div className="mb-3 mx-datepicker mx-datepicker-range">
                          <DatePicker
                            id="user-register-date-range"
                            selectsRange
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(update) => setDateRange(update as [Date | null, Date | null])}
                            isClearable
                            dateFormat="dd/MM/yyyy"
                            className="mx-input"
                            wrapperClassName="d-block w-100"
                          />
                        </div>
                      </div>
                    )}
                    <div className="col-3">
                      <div style={{ width: "100%", minHeight: "24px" }} aria-hidden="true" />
                      <button type="submit" id="loaddata" className="btn btn-primary" disabled={loading}>
                        {loading ? "Loading..." : "Load"}
                      </button>{" "}
                      <button type="button" id="reset" className="btn btn-light" onClick={handleReset}>
                        Reset
                      </button>{" "}
                      <div className="d-inline-block">
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={handleExportExcel}
                          disabled={sortedFilteredList.length === 0}
                        >
                          <i className="fas fa-file-excel" />
                        </button>
                      </div>{" "}
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleExportPdf}
                        disabled={sortedFilteredList.length === 0}
                      >
                        <i className="fas fa-file-pdf" />
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <div id="tickets-table_length" className="dataTables_length">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="d-inline-flex align-items-center">
                      Show&nbsp;
                      <select
                        className="custom-select custom-select-sm"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        {PAGE_SIZE_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      &nbsp;entries
                    </label>
                  </div>
                </div>
                <div className="col-sm-12 col-md-6">
                  <div id="tickets-table_filter" className="dataTables_filter text-md-right">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="d-inline-flex align-items-center">
                      Search:
                      <input
                        type="search"
                        placeholder="Search..."
                        className="form-control form-control-sm ml-2 form-control"
                        value={searchText}
                        onChange={(e) => {
                          setSearchText(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="table-responsive mb-0" style={{ whiteSpace: "nowrap" }}>
                <div className="table no-footer table-hover table-responsive-sm">
                  <table id="accStmtTable" role="table" aria-busy="false" aria-colcount={11} className="table b-table table-bordered">
                    <thead>
                      <tr role="row">
                        <th
                          role="columnheader"
                          scope="col"
                          tabIndex={0}
                          aria-colindex={1}
                          aria-sort={getHeaderAriaSort("user_name")}
                          className="position-relative"
                          onClick={() => handleSort("user_name")}
                          onKeyDown={(e) => handleSortKeyDown(e, "user_name")}
                        >
                          <div>User Name</div>
                          <span className="sr-only">{getHeaderHint("user_name")}</span>
                        </th>
                        <th
                          role="columnheader"
                          scope="col"
                          tabIndex={0}
                          aria-colindex={2}
                          aria-sort={getHeaderAriaSort("agent_name")}
                          className="position-relative"
                          onClick={() => handleSort("agent_name")}
                          onKeyDown={(e) => handleSortKeyDown(e, "agent_name")}
                        >
                          <div>Agent Name</div>
                          <span className="sr-only">{getHeaderHint("agent_name")}</span>
                        </th>
                        <th
                          role="columnheader"
                          scope="col"
                          tabIndex={0}
                          aria-colindex={3}
                          aria-sort={getHeaderAriaSort("created_date")}
                          className="position-relative"
                          onClick={() => handleSort("created_date")}
                          onKeyDown={(e) => handleSortKeyDown(e, "created_date")}
                        >
                          <div>Created Date</div>
                          <span className="sr-only">{getHeaderHint("created_date")}</span>
                        </th>
                        <th
                          role="columnheader"
                          scope="col"
                          tabIndex={0}
                          aria-colindex={4}
                          aria-sort={getHeaderAriaSort("last_login")}
                          className="position-relative"
                          onClick={() => handleSort("last_login")}
                          onKeyDown={(e) => handleSortKeyDown(e, "last_login")}
                        >
                          <div>Last Login</div>
                          <span className="sr-only">{getHeaderHint("last_login")}</span>
                        </th>
                        <th
                          role="columnheader"
                          scope="col"
                          tabIndex={0}
                          aria-colindex={5}
                          aria-sort={getHeaderAriaSort("first_deposit_date")}
                          className="position-relative"
                          onClick={() => handleSort("first_deposit_date")}
                          onKeyDown={(e) => handleSortKeyDown(e, "first_deposit_date")}
                        >
                          <div>First Deposit Date</div>
                          <span className="sr-only">{getHeaderHint("first_deposit_date")}</span>
                        </th>
                        <th
                          role="columnheader"
                          scope="col"
                          tabIndex={0}
                          aria-colindex={6}
                          aria-sort={getHeaderAriaSort("last_deposit_date")}
                          className="position-relative"
                          onClick={() => handleSort("last_deposit_date")}
                          onKeyDown={(e) => handleSortKeyDown(e, "last_deposit_date")}
                        >
                          <div>Last Deposit Date</div>
                          <span className="sr-only">{getHeaderHint("last_deposit_date")}</span>
                        </th>
                        <th
                          role="columnheader"
                          scope="col"
                          tabIndex={0}
                          aria-colindex={7}
                          aria-sort={getHeaderAriaSort("deposit")}
                          className="position-relative"
                          onClick={() => handleSort("deposit")}
                          onKeyDown={(e) => handleSortKeyDown(e, "deposit")}
                        >
                          <div>Deposit</div>
                          <span className="sr-only">{getHeaderHint("deposit")}</span>
                        </th>
                        <th
                          role="columnheader"
                          scope="col"
                          tabIndex={0}
                          aria-colindex={8}
                          aria-sort={getHeaderAriaSort("sports_balance")}
                          className="position-relative text-right"
                          onClick={() => handleSort("sports_balance")}
                          onKeyDown={(e) => handleSortKeyDown(e, "sports_balance")}
                        >
                          <div>Sports Balance</div>
                          <span className="sr-only">{getHeaderHint("sports_balance")}</span>
                        </th>
                        <th
                          role="columnheader"
                          scope="col"
                          tabIndex={0}
                          aria-colindex={9}
                          aria-sort={getHeaderAriaSort("casino_balance")}
                          className="position-relative text-right"
                          onClick={() => handleSort("casino_balance")}
                          onKeyDown={(e) => handleSortKeyDown(e, "casino_balance")}
                        >
                          <div>Casino Balance</div>
                          <span className="sr-only">{getHeaderHint("casino_balance")}</span>
                        </th>
                        <th
                          role="columnheader"
                          scope="col"
                          tabIndex={0}
                          aria-colindex={10}
                          aria-sort={getHeaderAriaSort("third_party_credit_balance")}
                          className="position-relative text-right"
                          onClick={() => handleSort("third_party_credit_balance")}
                          onKeyDown={(e) => handleSortKeyDown(e, "third_party_credit_balance")}
                        >
                          <div>Third Party Credit Balance</div>
                          <span className="sr-only">{getHeaderHint("third_party_credit_balance")}</span>
                        </th>
                        <th
                          role="columnheader"
                          scope="col"
                          tabIndex={0}
                          aria-colindex={11}
                          aria-sort={getHeaderAriaSort("sport_book_balance")}
                          className="position-relative text-right"
                          onClick={() => handleSort("sport_book_balance")}
                          onKeyDown={(e) => handleSortKeyDown(e, "sport_book_balance")}
                        >
                          <div>Sport Book Balance</div>
                          <span className="sr-only">{getHeaderHint("sport_book_balance")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedList.length === 0 ? (
                        <tr className="b-table-empty-row">
                          <td colSpan={11}>
                            <p className="text-center mb-0">No data available in table</p>
                          </td>
                        </tr>
                      ) : (
                        pagedList.map((row, idx) => (
                          <tr key={`${row.user_name}-${idx}`} role="row" aria-rowindex={idx + 1}>
                            <td aria-colindex={1}><div>{row.user_name}</div></td>
                            <td aria-colindex={2}><div>{row.agent_name}</div></td>
                            <td aria-colindex={3}><span>{row.created_date}</span></td>
                            <td aria-colindex={4}><span>{row.last_login}</span></td>
                            <td aria-colindex={5}><span>{row.first_deposit_date}</span></td>
                            <td aria-colindex={6}><span>{row.last_deposit_date}</span></td>
                            <td aria-colindex={7}><span>{row.deposit}</span></td>
                            <td aria-colindex={8}>
                              <div className={`text-right ${getBalanceClass(row.sports_balance)}`}><span>{row.sports_balance}</span></div>
                            </td>
                            <td aria-colindex={9}>
                              <div className={`text-right ${getBalanceClass(row.casino_balance)}`}><span>{row.casino_balance}</span></div>
                            </td>
                            <td aria-colindex={10}>
                              <div className={`text-right ${getBalanceClass(row.third_party_credit_balance)}`}><span>{row.third_party_credit_balance}</span></div>
                            </td>
                            <td aria-colindex={11}>
                              <div className="text-right"><span>{row.sport_book_balance}</span></div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="row pt-3">
                <div className="col">
                  <div className="dataTables_paginate paging_simple_numbers float-right">
                    <ul className="pagination pagination-rounded mb-0">
                      <ul
                        role="menubar"
                        aria-disabled="false"
                        aria-label="Pagination"
                        className="pagination dataTables_paginate paging_simple_numbers my-0 b-pagination justify-content-end"
                      >
                        <li role="presentation" className={`page-item ${isFirstPage ? "disabled" : ""}`}>
                          <button
                            role="menuitem"
                            type="button"
                            aria-label="Go to first page"
                            disabled={isFirstPage}
                            className="page-link"
                            onClick={() => setCurrentPage(1)}
                          >
                            &laquo;
                          </button>
                        </li>
                        <li role="presentation" className={`page-item ${isFirstPage ? "disabled" : ""}`}>
                          <button
                            role="menuitem"
                            type="button"
                            aria-label="Go to previous page"
                            disabled={isFirstPage}
                            className="page-link"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          >
                            &lsaquo;
                          </button>
                        </li>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <li
                            key={page}
                            role="presentation"
                            className={`page-item ${currentPage === page ? "active" : ""}`}
                          >
                            <button
                              role="menuitemradio"
                              type="button"
                              aria-label={`Go to page ${page}`}
                              aria-checked={currentPage === page}
                              aria-posinset={page}
                              aria-setsize={totalPages}
                              className="page-link"
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          </li>
                        ))}
                        <li role="presentation" className={`page-item ${isLastPage ? "disabled" : ""}`}>
                          <button
                            role="menuitem"
                            type="button"
                            aria-label="Go to next page"
                            disabled={isLastPage}
                            className="page-link"
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          >
                            &rsaquo;
                          </button>
                        </li>
                        <li role="presentation" className={`page-item ${isLastPage ? "disabled" : ""}`}>
                          <button
                            role="menuitem"
                            type="button"
                            aria-label="Go to last page"
                            disabled={isLastPage}
                            className="page-link"
                            onClick={() => setCurrentPage(totalPages)}
                          >
                            &raquo;
                          </button>
                        </li>
                      </ul>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRegisterDetailPage;
